import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FaEraser, FaCheck, FaTimes, FaFileSignature } from 'react-icons/fa';
import { api } from '../../../components/api/api';
import { Alert, AlertErro } from '../../../util/Alertas';

function ModalAssinaturaPedido({ pedido, onHide, onReload }) {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    // ref (e não state) para não perder traços entre renders
    const desenhandoRef = useRef(false);
    const [hasSignature, setHasSignature] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Mantém o tamanho interno do canvas igual ao tamanho exibido. Antes era medido só uma vez ao abrir
    // (durante a animação do modal), podendo ficar com poucos pixels e a assinatura não aparecer.
    const ajustarCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const rect = container.getBoundingClientRect();
        const largura = Math.round(rect.width);
        const altura = Math.round(rect.height);
        if (largura <= 0 || altura <= 0) return;
        if (canvas.width === largura && canvas.height === altura) return;

        // Preserva o que já foi desenhado
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;
        tempCanvas.getContext('2d').drawImage(canvas, 0, 0);

        canvas.width = largura;
        canvas.height = altura;

        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(tempCanvas, 0, 0);

        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#1e293b';
    }, []);

    useEffect(() => {
        ajustarCanvas();
        const observer = new ResizeObserver(() => ajustarCanvas());
        if (containerRef.current) observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [ajustarCanvas]);

    const getCoordinates = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        // Converte da posição na tela para os pixels internos do canvas
        return {
            x: (e.clientX - rect.left) * (canvas.width / (rect.width || 1)),
            y: (e.clientY - rect.top) * (canvas.height / (rect.height || 1))
        };
    };

    // Pointer events cobrem mouse, toque e caneta e permitem preventDefault (eventos de toque do React são passivos)
    const startDrawing = (e) => {
        e.preventDefault();
        ajustarCanvas();
        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.setPointerCapture?.(e.pointerId);
        const ctx = canvas.getContext('2d');
        const { x, y } = getCoordinates(e);

        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x, y);
        ctx.stroke();
        desenhandoRef.current = true;
        setHasSignature(true);
    };

    const draw = (e) => {
        if (!desenhandoRef.current) return;
        e.preventDefault();

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const { x, y } = getCoordinates(e);

        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDrawing = (e) => {
        if (!desenhandoRef.current) return;
        desenhandoRef.current = false;
        const canvas = canvasRef.current;
        if (canvas) {
            if (e?.pointerId !== undefined && canvas.hasPointerCapture?.(e.pointerId))
                canvas.releasePointerCapture(e.pointerId);
            canvas.getContext('2d').closePath();
        }
    };

    const handleClear = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        setHasSignature(false);
    };

    const handleSave = () => {
        const canvas = canvasRef.current;
        if (!canvas || !hasSignature) {
            Alert('Por favor, colete a assinatura antes de salvar.', false, true);
            return;
        }

        setIsSaving(true);

        canvas.toBlob(async (blob) => {
            if (!blob) {
                Alert('Erro ao gerar imagem da assinatura.', false);
                setIsSaving(false);
                return;
            }

            const fileName = `Assinatura_Pedido_${pedido.pedido_id}_${Date.now()}.png`;
            const file = new File([blob], fileName, { type: 'image/png' });

            const formData = new FormData();
            formData.append('files', file, fileName);

            api.post(`Arquivo/Novo/${pedido.pedido_id}`, formData, () => {
                // Atualiza o status do pedido para "Entregue" (ID 6). O api.post não retorna promise,
                // então o "await" antigo não esperava nada: a lista recarregava antes e erros eram ignorados.
                const statusData = new FormData();
                statusData.append('obj', JSON.stringify({
                    PedidoId: pedido.pedido_id,
                    StatusPedidoId: 6,
                    Motivo: 'Entregue com assinatura digital'
                }));

                api.post('/api/pedidos/AtualizarStatusPedido', statusData, () => {
                    setIsSaving(false);
                    onHide();
                    if (onReload) onReload();
                    Alert('Assinatura salva e pedido entregue com sucesso!', true);
                }, (err) => {
                    setIsSaving(false);
                    onHide();
                    if (onReload) onReload();
                    AlertErro(err, 'A assinatura foi salva, mas não foi possível marcar o pedido como entregue. Tente alterar o status novamente.');
                });
            }, (err) => {
                setIsSaving(false);
                AlertErro(err, 'Erro ao enviar arquivo de assinatura.');
            });
        }, 'image/png');
    };

    return (
        <Modal show={true} fullscreen={true} onHide={onHide} className="modal-assinatura-fullscreen">
            <Modal.Header closeButton style={{ background: '#0f172a', color: '#ffffff' }}>
                <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FaFileSignature style={{ color: '#38bdf8' }} />
                    Assinatura de Entrega - Pedido #{pedido?.pedido_id} ({pedido?.nomeCliente})
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ display: 'flex', flexDirection: 'column', padding: '15px', background: '#f8fafc' }}>
                {pedido?.produtos && pedido.produtos.length > 0 && (
                    <div style={{ marginBottom: '15px', background: '#fff', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        <h6 style={{ fontWeight: 'bold', color: '#1e293b' }}>Produtos do Pedido</h6>
                        <table className="table table-sm table-bordered" style={{ fontSize: '0.9rem', marginBottom: 0 }}>
                            <thead style={{ background: '#f1f5f9' }}>
                                <tr>
                                    <th>Produto</th>
                                    <th style={{ width: '80px', textAlign: 'center' }}>Qtde</th>
                                    <th style={{ width: '120px', textAlign: 'right' }}>Desconto</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pedido.produtos.map((prod, index) => {
                                    const desconto = prod.valor_desconto || (prod.desconto_unitario * prod.quantidade) || 0;
                                    return (
                                        <tr key={index}>
                                            <td>{prod.nome || `Produto ${prod.produto_id}`}</td>
                                            <td style={{ textAlign: 'center' }}>{prod.quantidade}</td>
                                            <td style={{ textAlign: 'right' }}>
                                                {desconto > 0 ? `R$ ${desconto.toFixed(2).replace('.', ',')}` : '-'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <div style={{ marginBottom: '10px', color: '#475569', fontSize: '0.95rem' }}>
                    <strong>Instruções:</strong> Solicite ao lojista/responsável para desenhar a assinatura no espaço abaixo usando o dedo ou mouse.
                </div>

                <div
                    ref={containerRef}
                    style={{
                        flex: 1,
                        minHeight: '300px',
                        position: 'relative',
                        border: '2px dashed #94a3b8',
                        borderRadius: '12px',
                        backgroundColor: '#ffffff',
                        overflow: 'hidden',
                        touchAction: 'none'
                    }}
                >
                    {!hasSignature && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: '#cbd5e1',
                                fontSize: '1.5rem',
                                fontWeight: 'bold',
                                pointerEvents: 'none',
                                userSelect: 'none'
                            }}
                        >
                            Assine aqui
                        </div>
                    )}

                    <canvas
                        ref={canvasRef}
                        onPointerDown={startDrawing}
                        onPointerMove={draw}
                        onPointerUp={stopDrawing}
                        onPointerCancel={stopDrawing}
                        onPointerLeave={stopDrawing}
                        style={{ width: '100%', height: '100%', cursor: 'crosshair', display: 'block', touchAction: 'none' }}
                    />
                </div>
            </Modal.Body>

            <Modal.Footer style={{ background: '#f1f5f9', display: 'flex', justifyContent: 'space-between' }}>
                <Button variant="outline-danger" onClick={handleClear} disabled={isSaving || !hasSignature}>
                    <FaEraser style={{ marginRight: '6px' }} /> Limpar Assinatura
                </Button>

                <div style={{ display: 'flex', gap: '10px' }}>
                    <Button variant="secondary" onClick={onHide} disabled={isSaving}>
                        <FaTimes style={{ marginRight: '6px' }} /> Cancelar
                    </Button>
                    <Button variant="success" onClick={handleSave} disabled={isSaving || !hasSignature}>
                        <FaCheck style={{ marginRight: '6px' }} /> {isSaving ? 'Salvando...' : 'Salvar Assinatura'}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}

export default ModalAssinaturaPedido;
